import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { qualifiedClientRefSchema } from '../eligibility/client-ref';
import { carePathwayTierSchema } from '../programs/schema';

// Governed vocabulary + wire contracts for the billing-trigger domain.
//
// Each constrained string domain is a single source of truth (ADR-056 Rule 8):
// the `const X = [...] as const` array drives BOTH the `text`+CHECK constraint
// rendered in the DB schema and the wire/contract schema here, so the DB and
// the validators cannot drift. Mirrors the eligibility/schema.ts pattern.

// ─── per-client billing trigger configuration ───────────────────────────────
// `standard` fires on the earliest defensible billing moment (substance
// selection + matched eligibility); `conservative` additionally waits for
// completed baseline capture. Absent/invalid config derives to `conservative`
// (fire only when unambiguous) — that defaulting lives at the config accessor,
// not here.
export const BILLING_TRIGGER_CONFIG = ['standard', 'conservative'] as const;
export const billingTriggerConfigSchema = z.enum(BILLING_TRIGGER_CONFIG);
export type BillingTriggerConfig = z.infer<typeof billingTriggerConfigSchema>;

// ─── ledger row provenance ──────────────────────────────────────────────────
// Who wrote a `billing_triggers` row when it was not the trigger consumer.
// NULL is the consumer's own fire; `legacy_import` is an episode the member
// migration landed as already billed in Pelago 1.0 — a row the warehouse
// must not count as a Galapagos fire.
export const BILLING_TRIGGER_SOURCE = ['legacy_import'] as const;
export const billingTriggerSourceSchema = z.enum(BILLING_TRIGGER_SOURCE);
export type BillingTriggerSource = z.infer<typeof billingTriggerSourceSchema>;

// The typed content of `clients.billing_config` (jsonb, `.$type<>()` per
// ADR-066), validated at the repo boundary. `.strict()` so an unknown key
// makes the whole config invalid (→ the conservative default) rather than
// being silently carried. Long-term this value syncs from the internaltools
// per-client config store through the checking-layer seam; the shape here is
// the portable contract.
export const clientBillingConfigSchema = z
  .object({
    billing_trigger: billingTriggerConfigSchema,
  })
  .strict();
export type ClientBillingConfig = z.infer<typeof clientBillingConfigSchema>;

// ─── event payload (ADR-052) ────────────────────────────────────────────────
// Strict pointer-shaped payload: ids + governed-enum lifecycle facts only.
// Submitted identity, answer values, and baseline day counts never transit the
// bus — `.strict()` makes such a leak a test failure, not a review catch.
// `billingTriggerId` is the end-to-end idempotency key (ADR-052 Rule 3): a
// crash-window duplicate publish carries the same id and is downstream-dedupable.
export const billingTriggerFiredPayloadSchema = z
  .object({
    billingTriggerId: z.string().uuid(),
    memberId: memberIdSchema,
    // Nullable during the clients-table expand/contract: clientRef below is the
    // billed-sponsor key; this field drops in the contract promotion.
    clientId: z.string().uuid().nullable(),
    // Provider-qualified external client ref (`<source>:<ref>`) — internaltools
    // owns company data, so the payload carries the provider's own ref. Null
    // only for a fire against a grant row written during the deploy window
    // before the backfill covered it (or a republished pre-switch ledger row).
    clientRef: qualifiedClientRefSchema.nullable(),
    programDefinitionId: z.string().uuid(),
    careLevel: carePathwayTierSchema,
    triggerConfig: billingTriggerConfigSchema,
    zeroBaselineApplied: z.boolean(),
    // Injected by buildAnnotatedDomainEvent from the analytics catalog —
    // closed to that exact value, never free text.
    status: z.literal('fired').optional(),
  })
  .strict();
export type BillingTriggerFiredPayload = z.infer<typeof billingTriggerFiredPayloadSchema>;

// ─── billed-tier series (billing_tier_events) ───────────────────────────────
// Why a series row exists. `anchor` is row 1 (the tier at fire time, mirroring
// `billing_triggers.care_level`); every later row is a billable change.
export const BILLING_TIER_EVENT_REASON = [
  'anchor',
  'baseline_adjustment',
  'clinician_retier',
  'diagnosis',
  'manual_correction',
  'substance_added',
] as const;
export const billingTierEventReasonSchema = z.enum(BILLING_TIER_EVENT_REASON);
export type BillingTierEventReason = z.infer<typeof billingTierEventReasonSchema>;

// `rule_version` for anchor rows the tier rules never computed: an imported
// Pelago 1.0 episode, or a Galapagos fire that predates the series.
export const BILLING_TIER_BACKFILL_RULE_VERSION = {
  legacyImport: 'legacy_import',
  preSeries: 'pre-series',
} as const;

// The typed content of `billing_tier_events.inputs` (jsonb, ADR-066). Booleans
// and the program keys the rule read; never answer values, day counts or free
// text, so nothing a member typed lands on a billing row.
export const billingTierEventInputsSchema = z
  .object({
    sessionCompletedAtFire: z.boolean().optional(),
    fullZeroBaseline: z.boolean().optional(),
    // The substance program a `substance_added` row bills the BA anchor at.
    basisProgramKey: z.string().min(1).optional(),
    // Set (true) only when a no-disorder diagnosis moved the tier below the
    // program default. Never the kind or the severity.
    diagnosisNoDisorder: z.boolean().optional(),
    // Set (true) only when the row's predecessor was a `diagnosis` row or had
    // its tier set by the diagnosis rule, so `previous_tier` repeats it.
    previousTierFromDiagnosis: z.boolean().optional(),
  })
  .strict();
export type BillingTierEventInputs = z.infer<typeof billingTierEventInputsSchema>;

// Strict pointer payload: every field is read from the stored row, so
// rebuilding the event for a row later yields the same payload. `actor_ref`
// and `inputs` stay on the row.
export const billingTierChangedPayloadSchema = z
  .object({
    billingTierEventId: z.string().uuid(),
    billingTriggerId: z.string().uuid(),
    memberId: memberIdSchema,
    programDefinitionId: z.string().uuid(),
    tier: carePathwayTierSchema,
    previousTier: carePathwayTierSchema.nullable(),
    reason: billingTierEventReasonSchema,
    effectiveAt: z.string().datetime({ offset: true }),
    ruleVersion: z.string().min(1),
    supersedes: z.string().uuid().nullable(),
    // Injected by buildAnnotatedDomainEvent from the analytics catalog.
    status: z.literal('recorded').optional(),
  })
  .strict();
export type BillingTierChangedPayload = z.infer<typeof billingTierChangedPayloadSchema>;
